import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, CheckCircle2, ChevronDown, ChevronsUpDown, Copy, Loader2, Megaphone, Paperclip, Pencil, RefreshCw, XCircle } from "lucide-react";
import axios from "axios";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RichTextEditor } from "@/components/broadcast/rich-text-editor";
import {
  CUSTOMER_STAGES,
  CUSTOMER_STAGE_FILTER_ENABLED,
  DEFAULT_CUSTOMER_STAGES,
  type CustomerStage,
} from "@/lib/constants/customer-stages";
import { normalizeEditorMarkdown } from "@/lib/markdown-utils";
import { cn } from "@/lib/utils";

import { backofficeClient } from "@/lib/api/backoffice-client";
import { useAllTenants } from "@/hooks/use-tenants";
import { useCellAudience, useCellHistory } from "@/hooks/use-broadcast-cells";
import type { TenantRead } from "@harmonyso/tenants-sdk";
import { CellFailuresAlert, CellTenantsUnavailable } from "@/components/cells/cell-failures-alert";
import { CELLS, tenantLabel, tenantOptionValue } from "@/lib/cells/cells";
import {
  NOT_SENT_PREFIX,
  mergeRetry,
  sendToCells,
  type BroadcastHistoryItem,
  type BroadcastResponse,
  type BroadcastTenantResult,
} from "@/lib/broadcast/fan-out";

export const Route = createFileRoute("/broadcast")({
  component: BroadcastPage,
});

const BROADCAST_TYPES: { value: string; label: string }[] = [
  { value: "maintenance", label: "Maintenance" },
  { value: "outage", label: "Outage" },
  { value: "whats_new", label: "What's New" },
  { value: "event", label: "Event" },
  { value: "tutorial", label: "Tutorial" },
  { value: "invitation", label: "Invitation" },
];

const TYPE_LABELS = new Map(BROADCAST_TYPES.map((t) => [t.value, t.label]));

function errorDetail(e: unknown): string {
  const detail = axios.isAxiosError(e)
    ? ((e.response?.data as { detail?: unknown } | undefined)?.detail ?? e.message)
    : String(e);
  return typeof detail === "string" ? detail : JSON.stringify(detail);
}

// Client-minted idempotency key (max 12 chars, matching the backend column).
// Minted once per logical broadcast and reused on every retry — including a
// blind resend after a timeout — so the backend can dedupe per tenant.
function mintIdempotentId(): string {
  const bytes = new Uint8Array(9);
  crypto.getRandomValues(bytes);
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  let id = "";
  for (const b of bytes) {
    id += alphabet[b % alphabet.length];
  }
  return id.slice(0, 12);
}

interface BroadcastFormState {
  title: string;
  body: string;
  announcementType: string;
  ctaLink: string;
  schedule: "now" | "scheduled";
  publishAt: string;
  expireAt: string;
  portalBanner: boolean;
  dmEmployees: boolean;
  emailEmployees: boolean;
  tenantScope: "stages" | "subset" | "all";
  tenantIds: string[];
  // Which Salesforce stages a stage-targeted send reaches. Used only when
  // tenantScope is "stages", which needs Salesforce — production only.
  customerStages: CustomerStage[];
}

const INITIAL_FORM: BroadcastFormState = {
  title: "",
  body: "",
  announcementType: "maintenance",
  ctaLink: "",
  schedule: "now",
  publishAt: "",
  expireAt: "",
  portalBanner: false,
  dmEmployees: true,
  emailEmployees: false,
  // Stage targeting needs Salesforce, which only production has. Without
  // it the composer offers every tenant instead, expanded client-side
  // into an explicit list — see buildRequestBody.
  tenantScope: CUSTOMER_STAGE_FILTER_ENABLED ? "stages" : "all",
  tenantIds: [],
  customerStages: DEFAULT_CUSTOMER_STAGES,
};

// Substituted per tenant by the backend fan-out, so each recipient's link
// points at their own domain.
const TENANT_SUBDOMAIN_PLACEHOLDER = "{{tenant_subdomain}}";

// Messengers silently refuse to open a scheme-less URL (Teams renders the
// button but the click does nothing), so a bare domain gets https:// and
// anything that still doesn't parse is rejected before send.
function normalizeCtaLink(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  // Validate with the placeholder stood in by a plausible subdomain; the
  // returned link keeps the placeholder for the backend to substitute.
  const probe = candidate.replaceAll(TENANT_SUBDOMAIN_PLACEHOLDER, "tenant");
  try {
    const url = new URL(probe);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    // The URL parser is lenient — it percent-encodes junk ("https://not a
    // url") instead of throwing — so require a dotted hostname on top.
    const labels = url.hostname.split(".");
    const isLabel = (label: string) =>
      label.length > 0 &&
      label.length <= 63 &&
      !label.startsWith("-") &&
      !label.endsWith("-") &&
      /^[a-z0-9-]+$/i.test(label);
    if (labels.length < 2 || !labels.every(isLabel)) return null;
    return candidate;
  } catch {
    return null;
  }
}

// The named tenants a send reaches, or undefined for a stage audience.
function audienceTenantIds(
  form: BroadcastFormState,
  allTenantIds: string[],
  tenantIdsOverride?: string[],
): string[] | undefined {
  // A retry of failed tenants names them explicitly, which overrides
  // whichever audience the original send used.
  //
  // "all" is a DEV convenience: with no Salesforce there is no stage to
  // target by, so the composer expands every known tenant into an
  // explicit list. The API has no "everyone" audience — the reach still
  // travels on the wire as the tenants it actually means.
  return (
    tenantIdsOverride ??
    (form.tenantScope === "subset"
      ? form.tenantIds
      : form.tenantScope === "all"
        ? allTenantIds
        : undefined)
  );
}

function buildRequestBody(
  form: BroadcastFormState,
  idempotentId: string,
  tenantIds: string[] | undefined,
) {
  return {
    idempotent_id: idempotentId,
    title: form.title,
    // Last line of defence: the editor normalizes as you type, but a body
    // can also reach the form without passing through onChange (cloning an
    // older broadcast loads the stored text verbatim), and a stored body
    // predates the fix. Normalizing at the send boundary covers every path.
    body: normalizeEditorMarkdown(form.body),
    announcement_type: form.announcementType,
    cta_link: normalizeCtaLink(form.ctaLink) ?? undefined,
    publish_at: form.schedule === "scheduled" && form.publishAt ? new Date(form.publishAt).toISOString() : undefined,
    expire_at: form.expireAt ? new Date(form.expireAt).toISOString() : undefined,
    delivery_channels: {
      portal_banner: form.portalBanner,
      dm_employees: form.dmEmployees,
      email_employees: form.dmEmployees && form.emailEmployees,
      slack_channels: [],
      teams_channels: [],
    },
    // Exactly one audience: named tenants, or customer stages. The
    // backend rejects both or neither, so there is no implicit "everyone".
    ...(tenantIds
      ? { tenant_ids: tenantIds }
      : { customer_stages: form.customerStages }),
  };
}

// Mirrors the backend's min_expiration_minutes rule so the composer
// rejects a too-close expiry before the fan-out does.
const MIN_EXPIRY_MINUTES = 15;

const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
const ATTACHMENT_ACCEPT =
  "image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.md,.json,.xml";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

// The history table aggregates per-tenant copies, so it carries content
// fields only — a clone pre-fills those and leaves delivery channels,
// targeting and schedule at their defaults for the operator to re-choose.
// History returns ONE copy's link, but broadcasts sent with
// {{tenant_subdomain}} store a per-tenant RESOLVED link on every copy —
// cloning that verbatim would resend one tenant's domain to everyone.
// If the link's first host label is a known tenant subdomain, turn it
// back into the placeholder so the clone broadcasts correctly again.
function templatizeCtaLink(link: string | null, tenants: TenantRead[] | undefined): string {
  if (!link) return "";
  try {
    const host = new URL(link).hostname;
    const [first, ...rest] = host.split(".");
    if (
      first &&
      rest.length > 0 &&
      (tenants ?? []).some((t) => t.subdomain === first)
    ) {
      return link.replace(`//${first}.`, `//${TENANT_SUBDOMAIN_PLACEHOLDER}.`);
    }
  } catch {
    // Not a parseable URL - leave it for the composer's validation.
  }
  return link;
}

function cloneToForm(
  item: BroadcastHistoryItem,
  tenants: TenantRead[] | undefined,
): Partial<BroadcastFormState> {
  return {
    title: item.title,
    body: normalizeEditorMarkdown(item.body),
    announcementType: item.announcement_type,
    ctaLink: templatizeCtaLink(item.cta_link, tenants),
  };
}

function BroadcastPage() {
  const [composerOpen, setComposerOpen] = useState(false);
  const [cloneSource, setCloneSource] = useState<BroadcastHistoryItem | null>(null);
  const queryClient = useQueryClient();
  const { data: tenants } = useAllTenants();
  const { data: history, isLoading, error: historyError, failures: historyFailures } =
    useCellHistory();

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Megaphone className="size-5" />
            Broadcasts
          </h1>
          <p className="text-sm text-muted-foreground">
            Messages sent from Harmony to customer platform admins.
          </p>
        </div>
        <Button
          onClick={() => {
            setCloneSource(null);
            setComposerOpen(true);
          }}
        >
          <Megaphone className="mr-1.5 size-4" />
          New broadcast
        </Button>
      </div>

      <CellFailuresAlert
        failures={historyFailures}
        heading={(cell) => `Broadcasts sent in ${cell.id} (${cell.residency}) aren't shown`}
      />

      {isLoading ? (
        <div className="flex items-center justify-center rounded-md border py-16">
          <Spinner />
        </div>
      ) : historyError ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load broadcast history.
        </div>
      ) : !history || history.items.length === 0 ? (
        <div className="rounded-md border py-16 text-center text-sm text-muted-foreground">
          No broadcasts sent yet.
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Publish at</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Delivered tenants</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.items.map((item) => (
                <TableRow key={item.idempotent_id}>
                  <TableCell className="max-w-72">
                    <span className="block truncate font-medium" title={item.title}>
                      {item.title}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{TYPE_LABELS.get(item.announcement_type) ?? item.announcement_type}</Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatDate(item.created_at)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {item.publish_at ? formatDate(item.publish_at) : "Immediately"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatDate(item.expire_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <span
                      className={cn(
                        item.published_count === item.tenant_count
                          ? "text-emerald-700 dark:text-emerald-400"
                          : "text-muted-foreground",
                      )}
                    >
                      {item.published_count} / {item.tenant_count}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      aria-label={`Clone "${item.title}"`}
                      title="Clone into a new broadcast"
                      onClick={() => {
                        setCloneSource(item);
                        setComposerOpen(true);
                      }}
                    >
                      <Copy className="size-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <BroadcastComposerDialog
        // Remount per clone source so each clone starts from a fresh
        // pre-filled form rather than inheriting composer state.
        key={cloneSource?.idempotent_id ?? "new"}
        open={composerOpen}
        onOpenChange={setComposerOpen}
        initialForm={cloneSource ? cloneToForm(cloneSource, tenants) : undefined}
        onSent={() => {
          void queryClient.invalidateQueries({ queryKey: ["broadcast-history"] });
        }}
      />
    </div>
  );
}

function BroadcastComposerDialog({
  open,
  onOpenChange,
  onSent,
  initialForm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSent: () => void;
  initialForm?: Partial<BroadcastFormState>;
}) {
  const [form, setForm] = useState<BroadcastFormState>({ ...INITIAL_FORM, ...initialForm });
  const [isSending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BroadcastResponse | null>(null);
  const [tenantPopoverOpen, setTenantPopoverOpen] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const idempotentIdRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Every cell's tenants: each cell's backoffice delivers to its own.
  const { data: tenants, isLoading: tenantsLoading, failures: tenantFailures } = useAllTenants();
  const allTenantIds = (tenants ?? []).map((t) => t.id);

  // Who the chosen stages actually resolve to. Resolved by the SAME
  // backend call the send uses, so the count shown here is the count that
  // gets messaged rather than an estimate.
  // Each cell resolves its own customers for the stages.
  const {
    data: audience,
    isFetching: audienceLoading,
    error: audienceError,
    failures: audienceFailures,
  } = useCellAudience(form.customerStages, form.tenantScope === "stages" && form.customerStages.length > 0);

  const tenantLabelById = useMemo(() => new Map((tenants ?? []).map((t) => [t.id, tenantLabel(t)])), [tenants]);

  const update = (patch: Partial<BroadcastFormState>) => {
    // Edited content is a NEW logical broadcast — a stale key would make
    // the backend silently return the old rows instead of sending it.
    idempotentIdRef.current = null;
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const toggleTenant = (tenantId: string) => {
    update({
      tenantIds: form.tenantIds.includes(tenantId)
        ? form.tenantIds.filter((id) => id !== tenantId)
        : [...form.tenantIds, tenantId],
    });
  };

  const toggleStage = (stage: CustomerStage) => {
    update({
      customerStages: form.customerStages.includes(stage)
        ? form.customerStages.filter((s) => s !== stage)
        : [...form.customerStages, stage],
    });
  };

  const resetComposer = () => {
    setResult(null);
    setError(null);
    idempotentIdRef.current = null;
    setForm(INITIAL_FORM);
    setAttachments([]);
  };

  const addFiles = (fileList: FileList | null) => {
    if (!fileList?.length) return;
    const incoming = Array.from(fileList);
    const oversized = incoming.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (oversized) {
      setError(`"${oversized.name}" exceeds the 20MB attachment limit`);
      return;
    }
    if (attachments.length + incoming.length > MAX_ATTACHMENTS) {
      setError(`At most ${MAX_ATTACHMENTS} attachments allowed`);
      return;
    }
    setError(null);
    // Changed content = a new logical broadcast, same as editing a field.
    idempotentIdRef.current = null;
    setAttachments((prev) => [...prev, ...incoming]);
  };

  const removeFile = (index: number) => {
    idempotentIdRef.current = null;
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Evaluated per render, so a deadline that was fine when typed can turn
  // invalid while the composer sits open — the next interaction re-checks.
  const expiryError = (() => {
    if (!form.expireAt) return null;
    const expireAt = new Date(form.expireAt).getTime();
    if (Number.isNaN(expireAt)) return null;
    const reference =
      form.schedule === "scheduled" && form.publishAt ? new Date(form.publishAt).getTime() : Date.now();
    if (expireAt - reference < MIN_EXPIRY_MINUTES * 60 * 1000) {
      return `Expiry must be at least ${String(MIN_EXPIRY_MINUTES)} minutes after ${
        form.schedule === "scheduled" ? "the publish time" : "now"
      }`;
    }
    return null;
  })();

  const ctaError =
    form.ctaLink.trim() !== "" && normalizeCtaLink(form.ctaLink) === null
      ? "Enter a valid http(s) link"
      : null;

  const isValid =
    form.title.trim().length >= 3 &&
    form.body.trim().length >= 3 &&
    (form.portalBanner || form.dmEmployees) &&
    (form.tenantScope === "subset"
      ? form.tenantIds.length > 0
      : form.tenantScope === "all"
        ? // "All" with a cell missing would quietly mean "all but that cell".
          allTenantIds.length > 0 && tenantFailures.length === 0
        : // A stage send with a cell's audience unresolved would skip that cell.
          form.customerStages.length > 0 && (CELLS.length === 1 || audienceFailures.length === 0)) &&
    (form.schedule === "now" || form.publishAt !== "") &&
    expiryError === null &&
    ctaError === null;

  // `retryCellIds` resends a stage audience only to the cells it failed in.
  const send = async (tenantIdsOverride?: string[], retryCellIds?: string[]) => {
    setSending(true);
    setError(null);
    const idempotentId = (idempotentIdRef.current ??= mintIdempotentId());
    const post = async (tenantIds: string[] | undefined, routeCellId?: string) => {
      const formData = new FormData();
      formData.append("payload", JSON.stringify(buildRequestBody(form, idempotentId, tenantIds)));
      for (const file of attachments) {
        formData.append("files", file);
      }
      const response = await backofficeClient.post<BroadcastResponse>("/broadcasts", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        routeCellId,
      });
      return response.data;
    };
    try {
      const response = await sendToCells({
        idempotentId,
        tenantIds: audienceTenantIds(form, allTenantIds, tenantIdsOverride),
        tenants: tenants ?? [],
        post,
        errorDetail,
        unavailableCellIds: tenantFailures.map(({ cell }) => cell.id),
        ...(retryCellIds && { cells: CELLS.filter((cell) => retryCellIds.includes(cell.id)) }),
      });
      setResult((previous) => (retryCellIds && previous ? mergeRetry(previous, response) : response));
      onSent();
    } catch (e) {
      setError(errorDetail(e));
    } finally {
      setSending(false);
    }
  };

  const resultLabel = (r: BroadcastTenantResult) => tenantLabelById.get(r.tenant_id) ?? r.company ?? r.tenant_id;

  // A tenant no cell has wasn't sent, and a retry can't place it either.
  const failedTenantIds =
    result?.results
      .filter((r) => !r.success && r.error !== "Unknown tenant" && !r.error?.startsWith(NOT_SENT_PREFIX))
      .map((r) => r.tenant_id) ?? [];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closing the dialog with the modal popover still open leaves a
        // stale pointer-events lock on <body> — collapse the popover first.
        if (!next) setTenantPopoverOpen(false);
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        onEscapeKeyDown={(e) => {
          // Escape with an open popover (tenant picker, emoji) should
          // close just the popover, not throw away the whole composer.
          if (document.querySelector('[data-slot=popover-content][data-state="open"]')) {
            e.preventDefault();
            setTenantPopoverOpen(false);
          }
        }}
      >
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Megaphone className="size-5" />
                Broadcast Sent
              </DialogTitle>
              <DialogDescription>
                {result.succeeded} of {result.total} tenants received the broadcast
                {result.cellErrors?.length ? ", not counting the cells it wasn't sent in." : "."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {result.cellErrors && result.cellErrors.length > 0 && (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
                  <p className="mb-2 font-medium text-destructive">
                    Not sent in {result.cellErrors.length === 1 ? "this cell" : "these cells"}:
                  </p>
                  <ul className="space-y-1">
                    {result.cellErrors.map(({ cellId, error: cellError }) => (
                      <li key={cellId} className="flex items-start gap-2 text-destructive">
                        <XCircle className="mt-0.5 size-3.5 shrink-0" />
                        <span>
                          {cellId}: {cellError}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.failed > 0 && (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
                  <p className="mb-2 font-medium text-destructive">{result.failed} tenant(s) failed:</p>
                  <ul className="space-y-1">
                    {result.results
                      .filter((r) => !r.success)
                      .map((r) => (
                        <li key={r.tenant_id} className="flex items-start gap-2 text-destructive">
                          <XCircle className="mt-0.5 size-3.5 shrink-0" />
                          <span>
                            {resultLabel(r)}: {r.error}
                          </span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
              {result.succeeded > 0 && (
                <details className="rounded-md border border-emerald-600/40 bg-emerald-500/10 p-3 text-sm" open={result.failed > 0}>
                  <summary className="flex cursor-pointer items-center gap-2 font-medium text-emerald-700 dark:text-emerald-400">
                    <CheckCircle2 className="size-4" />
                    {result.failed === 0 && !result.cellErrors?.length
                      ? "Delivered to all target tenants"
                      : `Delivered to ${String(result.succeeded)} tenant(s)`}
                  </summary>
                  <ul className="mt-2 space-y-1 pl-6">
                    {result.results
                      .filter((r) => r.success)
                      .map((r) => (
                        <li key={r.tenant_id} className="text-emerald-700 dark:text-emerald-400">
                          {resultLabel(r)}
                        </li>
                      ))}
                  </ul>
                </details>
              )}
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="flex gap-2">
                {result.cellErrors && result.cellErrors.length > 0 && (
                  <Button
                    onClick={() => void send(undefined, result.cellErrors?.map(({ cellId }) => cellId))}
                    disabled={isSending}
                    variant="outline"
                    size="sm"
                  >
                    <RefreshCw className="mr-1.5 size-3.5" />
                    {isSending ? "Retrying..." : "Retry failed cells"}
                  </Button>
                )}
                {failedTenantIds.length > 0 && (
                  <Button onClick={() => void send(failedTenantIds)} disabled={isSending} variant="outline" size="sm">
                    <RefreshCw className="mr-1.5 size-3.5" />
                    {isSending ? "Retrying..." : "Retry failed tenants"}
                  </Button>
                )}
                {result.failed > 0 && (
                  <Button
                    onClick={() => {
                      // Back to the form with the draft intact. Content
                      // edits mint a new idempotent_id (see update()), so
                      // an edited resend is a new logical broadcast, while
                      // an unedited one still dedupes per tenant.
                      setResult(null);
                      setError(null);
                    }}
                    variant="outline"
                    size="sm"
                  >
                    <Pencil className="mr-1.5 size-3.5" />
                    Edit broadcast
                  </Button>
                )}
                <Button onClick={resetComposer} variant="outline" size="sm">
                  Compose another
                </Button>
                <Button
                  onClick={() => {
                    resetComposer();
                    onOpenChange(false);
                  }}
                  variant="secondary"
                  size="sm"
                >
                  Done
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Megaphone className="size-5" />
                Broadcast Message
              </DialogTitle>
              <DialogDescription>Compose a message for platform admins across tenants.</DialogDescription>
            </DialogHeader>

            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-950/20">
              <div className="flex">
                <AlertTriangle className="mt-0.5 mr-2 size-5 text-yellow-600 dark:text-yellow-400" />
                <div className="text-sm text-yellow-800 dark:text-yellow-200">
                  <p className="font-medium">This sends a message to customer platform admins</p>
                  <p>
                    The broadcast is delivered as "Harmony" to the platform admins of the selected tenants. Double-check
                    content before sending.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="broadcast-title">Title</Label>
                <Input
                  id="broadcast-title"
                  value={form.title}
                  onChange={(e) => { update({ title: e.target.value }); }}
                  placeholder="Scheduled maintenance on Sunday"
                  maxLength={255}
                />
              </div>

              <div className="space-y-2">
                <Label>Message</Label>
                <RichTextEditor
                  value={form.body}
                  onChange={(markdown) => { update({ body: markdown }); }}
                  placeholder="We will be performing scheduled maintenance..."
                  onFileSelect={() => fileInputRef.current?.click()}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ATTACHMENT_ACCEPT}
                  className="hidden"
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                {attachments.length > 0 && (
                  <ul className="space-y-1">
                    {attachments.map((file, index) => (
                      <li
                        key={`${file.name}-${String(index)}`}
                        className="flex items-center gap-2 rounded-md border px-2 py-1 text-sm"
                      >
                        <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate">{file.name}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">{formatBytes(file.size)}</span>
                        <button
                          type="button"
                          aria-label={`Remove ${file.name}`}
                          className="shrink-0 cursor-pointer text-muted-foreground hover:text-destructive"
                          onClick={() => { removeFile(index); }}
                        >
                          <XCircle className="size-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={form.announcementType} onValueChange={(value) => { update({ announcementType: value }); }}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BROADCAST_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="broadcast-cta">CTA link (optional)</Label>
                  <Input
                    id="broadcast-cta"
                    value={form.ctaLink}
                    aria-invalid={ctaError !== null}
                    onChange={(e) => { update({ ctaLink: e.target.value }); }}
                    placeholder="https://status.harmony.io"
                  />
                  {ctaError && <p className="text-xs text-destructive">{ctaError}</p>}
                  <p className="text-xs text-muted-foreground">
                    Use {"{{tenant_subdomain}}"} to point each tenant at its own domain,
                    e.g. https://{"{{tenant_subdomain}}"}.harmony.io
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Delivery channels</Label>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.dmEmployees}
                      onCheckedChange={(checked) => { update({ dmEmployees: checked === true }); }}
                    />
                    Direct message (Slack / Teams DM)
                  </label>
                  <label className="flex items-center gap-2 pl-6 text-sm">
                    <Checkbox
                      checked={form.emailEmployees}
                      disabled={!form.dmEmployees}
                      onCheckedChange={(checked) => { update({ emailEmployees: checked === true }); }}
                    />
                    Also send email
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.portalBanner}
                      onCheckedChange={(checked) => { update({ portalBanner: checked === true }); }}
                    />
                    In-app portal banner
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Target tenants</Label>
                <RadioGroup
                  value={form.tenantScope}
                  onValueChange={(value) => { update({ tenantScope: value as BroadcastFormState["tenantScope"] }); }}
                  className="flex gap-4"
                >
                  {/* Targeting by stage needs Salesforce, which only
                      production has. Everywhere else the blunt "all"
                      stands in for it. */}
                  {CUSTOMER_STAGE_FILTER_ENABLED ? (
                    <div className="flex items-center gap-1.5">
                      <RadioGroupItem value="stages" id="tenants-stages" />
                      <Label htmlFor="tenants-stages" className="cursor-pointer text-sm font-normal">
                        By customer stage
                      </Label>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <RadioGroupItem value="all" id="tenants-all" />
                      <Label htmlFor="tenants-all" className="cursor-pointer text-sm font-normal">
                        All tenants
                      </Label>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <RadioGroupItem value="subset" id="tenants-subset" />
                    <Label htmlFor="tenants-subset" className="cursor-pointer text-sm font-normal">
                      Specific tenants
                    </Label>
                  </div>
                </RadioGroup>
                {form.tenantScope === "all" && (
                  <>
                    <p className="text-xs text-muted-foreground">
                      {tenantsLoading
                        ? "Loading tenants…"
                        : tenantFailures.length > 0
                          ? `${String(allTenantIds.length)} tenants listed, but not every cell's.`
                          : `Every tenant in this environment (${String(allTenantIds.length)}).`}
                    </p>
                    <CellFailuresAlert
                      failures={tenantFailures}
                      heading={(cell) =>
                        `${cell.id} (${cell.residency}) tenants couldn't be listed, so "All tenants" can't be sent yet. Sign in, or choose specific tenants.`
                      }
                      signInInNewTab
                    />
                  </>
                )}
                {form.tenantScope === "stages" && CUSTOMER_STAGE_FILTER_ENABLED && (
                  <div className="space-y-2 rounded-md border border-input p-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-normal">Customer stages</Label>
                      <button
                        type="button"
                        className="text-xs text-muted-foreground underline"
                        onClick={() => { update({ customerStages: DEFAULT_CUSTOMER_STAGES }); }}
                      >
                        Reset
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Reaches every tenant whose customer sits in a selected Salesforce stage.
                      Tenants with no Salesforce account — internal and test tenants among them —
                      match no stage; reach those by naming them under Specific tenants.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {CUSTOMER_STAGES.map((stage) => {
                        const selected = form.customerStages.includes(stage);
                        return (
                          <Badge
                            key={stage}
                            variant={selected ? "default" : "outline"}
                            className="cursor-pointer"
                            onClick={() => { toggleStage(stage); }}
                          >
                            {selected && <Check className="mr-1 size-3" />}
                            {stage}
                          </Badge>
                        );
                      })}
                    </div>
                    {CELLS.length > 1 && (
                      <CellFailuresAlert
                        failures={audienceFailures}
                        heading={(cell) =>
                          `${cell.id} (${cell.residency}) couldn't resolve its stage audience, so this can't be sent yet.`
                        }
                        signInInNewTab
                      />
                    )}
                    {form.customerStages.length === 0 && (
                      <p className="text-xs text-destructive">
                        Select at least one stage, or this broadcast reaches nobody.
                      </p>
                    )}
                    {form.customerStages.length > 0 && (
                      <Collapsible className="rounded-md border border-input bg-muted/30">
                        <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 px-2.5 py-2 text-left text-xs [&[data-state=open]>svg]:rotate-180">
                          <span>
                            {audienceLoading ? (
                              "Resolving audience…"
                            ) : audienceError ? (
                              <span className="text-destructive">
                                {CELLS.length > 1
                                  ? "Not every cell resolved its audience, so this can't be sent yet."
                                  : "Could not resolve the audience — the send will fail too."}
                              </span>
                            ) : audience ? (
                              <>
                                <span className="font-medium">{audience.total}</span>
                                {audience.total === 1 ? " tenant" : " tenants"} will receive this
                              </>
                            ) : (
                              "Audience not resolved yet"
                            )}
                          </span>
                          <ChevronDown className="size-3.5 shrink-0 opacity-60 transition-transform" />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          {audience && audience.tenants.length > 0 ? (
                            <ul className="max-h-48 space-y-0.5 overflow-y-auto border-t border-input px-2.5 py-2 text-xs">
                              {audience.tenants.map((t) => (
                                <li key={t.tenant_id} className="flex items-center justify-between gap-2">
                                  <span className="truncate">{t.company ?? t.tenant_id}</span>
                                  <span className="shrink-0 text-muted-foreground">{t.customer_stage}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="border-t border-input px-2.5 py-2 text-xs text-muted-foreground">
                              {audienceLoading
                                ? "Resolving…"
                                : "No tenant matches these stages. Tenants with no Salesforce account are never matched — reach those by naming them."}
                            </p>
                          )}
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </div>
                )}
                {form.tenantScope === "subset" && (
                  <div className="space-y-2">
                    {/* modal: inside a Dialog the page is pointer-events:none,
                        which would make the portaled popover unclickable */}
                    <Popover modal open={tenantPopoverOpen} onOpenChange={setTenantPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                          {form.tenantIds.length > 0
                            ? `${form.tenantIds.length} tenant(s) selected`
                            : tenantsLoading
                              ? "Loading tenants..."
                              : "Select tenants..."}
                          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search tenants..." />
                          <CellTenantsUnavailable failures={tenantFailures} signInInNewTab />
                          <CommandList>
                            <CommandEmpty>No tenants found.</CommandEmpty>
                            <CommandGroup>
                              {(tenants ?? []).map((tenant) => (
                                <CommandItem key={tenant.id} value={tenantOptionValue(tenant)} onSelect={() => { toggleTenant(tenant.id); }}>
                                  <Check
                                    className={cn(
                                      "mr-2 size-4",
                                      form.tenantIds.includes(tenant.id) ? "opacity-100" : "opacity-0",
                                    )}
                                  />
                                  {tenantLabel(tenant)}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {form.tenantIds.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {form.tenantIds.map((tenantId) => (
                          <Badge
                            key={tenantId}
                            variant="secondary"
                            className="cursor-pointer"
                            onClick={() => { toggleTenant(tenantId); }}
                          >
                            {tenantLabelById.get(tenantId) ?? tenantId}
                            <XCircle className="ml-1 size-3" />
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Schedule</Label>
                <RadioGroup
                  value={form.schedule}
                  onValueChange={(value) => { update({ schedule: value as "now" | "scheduled" }); }}
                  className="flex gap-4"
                >
                  <div className="flex items-center gap-1.5">
                    <RadioGroupItem value="now" id="schedule-now" />
                    <Label htmlFor="schedule-now" className="cursor-pointer text-sm font-normal">
                      Send now
                    </Label>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <RadioGroupItem value="scheduled" id="schedule-later" />
                    <Label htmlFor="schedule-later" className="cursor-pointer text-sm font-normal">
                      Schedule
                    </Label>
                  </div>
                </RadioGroup>
                <div className="grid grid-cols-2 gap-4">
                  {form.schedule === "scheduled" && (
                    <div className="space-y-1">
                      <Label htmlFor="broadcast-publish-at" className="text-xs text-muted-foreground">
                        Publish at
                      </Label>
                      <Input
                        id="broadcast-publish-at"
                        type="datetime-local"
                        value={form.publishAt}
                        onChange={(e) => { update({ publishAt: e.target.value }); }}
                      />
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label htmlFor="broadcast-expire-at" className="text-xs text-muted-foreground">
                      Expire at (optional)
                    </Label>
                    <Input
                      id="broadcast-expire-at"
                      type="datetime-local"
                      value={form.expireAt}
                      aria-invalid={expiryError !== null}
                      onChange={(e) => { update({ expireAt: e.target.value }); }}
                    />
                    {expiryError && <p className="text-xs text-destructive">{expiryError}</p>}
                  </div>
                </div>
              </div>

              {error && (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                  <div className="flex gap-2">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                </div>
              )}

              <Button onClick={() => void send()} disabled={!isValid || isSending}>
                {isSending ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : <Megaphone className="mr-1.5 size-4" />}
                {isSending ? "Sending..." : "Send broadcast"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
